import React, { useState } from 'react';
import { Btn, Msg, Section, Select } from '../../kit';
import { Check, Labeled, inputCls } from '../emailKit';
import { Content, Sender, num, parseEmails } from './shared';
import { withKeys } from '../designs/types';
import { ContentEditor } from '../content/ContentEditor';
import { EnvelopeFields } from '../content/EnvelopeFields';
import { SaveAsTemplate, TemplatePicker } from '../content/TemplatePicker';
import { EditableContent, LibraryRow, SourceContent, SourceRef } from '../content/types';

/**
 * Step 2 — what is sent, and from whom.
 *
 * An editable template (as it stands in "Templates (editable)", or a saved
 * template from Saved templates), a design from the e-mail builder (Email
 * designs: blocks, themed, buttons with their own text / link / colour), or a
 * mail written here: subject + a rich-text body (bold, italic, links, lists —
 * or its HTML source) with {placeholders} filled per person. The editor and
 * the sender fields are the shared ones (email/content), so MEMAC triggers and
 * Saved templates edit e-mail the same way.
 *
 * "Start from…" takes a saved template, a built-in e-mail, a past campaign or
 * a design: by reference (template mode, that key) or as a copy to change.
 * "Save as a template…" keeps a written or designed mail in Saved templates;
 * sending it as that template then names it on the ledger and the dashboard.
 * The From address is picked from the admin-approved list (Approved senders);
 * From name, Reply-To, CC and BCC are free per campaign.
 */

type Tpl = { key: string; name: string; trigger: string; sent: number };

export const ContentStep: React.FC<{
  content: Content; onContent: (patch: Partial<Content>) => void;
  templates: Tpl[];
  senders: Sender[]; sendersError: string;
  placeholders: { name: string; meaning: string }[];
  skipReceived: boolean; onSkip: (v: boolean) => void;
  /** Recipients as last counted, for the "copies per CC address" warning. */
  recipients: number | null;
  problem: string;
  /** The placeholders panel (PlaceholderFallbacks), under the content. */
  placeholdersPanel?: React.ReactNode;
}> = ({ content, onContent, templates, senders, sendersError, placeholders, skipReceived, onSkip,
        recipients, problem, placeholdersPanel }) => {
  const c = content;
  // A whole HTML document copied in (a past campaign) opens in the HTML source.
  const [rawHtml, setRawHtml] = useState(false);
  // Where the words came from, kept on a template saved from here.
  const [source, setSource] = useState<SourceRef | null>(null);
  // Templates picked or saved here that the tab's list (loaded once) does not have yet.
  const [extra, setExtra] = useState<Tpl[]>([]);
  const [picker, setPicker] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [savedTpl, setSavedTpl] = useState<LibraryRow | null>(null);
  const [note, setNote] = useState<{ kind: 'ok' | 'error' | 'warn'; text: string } | null>(null);

  const allTemplates = [...templates, ...extra.filter(e => !templates.some(t => t.key === e.key))];
  const active = allTemplates.find(t => t.key === c.key);
  const remember = (key: string, name: string) => {
    if (!templates.some(t => t.key === key)) {
      setExtra(xs => [...xs.filter(x => x.key !== key), { key, name, trigger: '', sent: 0 }]);
    }
  };

  // The campaign's content as the shared editor holds it, and back.
  const editable: EditableContent = {
    format: c.mode === 'design' ? 'design' : 'html',
    subject: c.subject, body: c.body, text: c.text, design: c.design, ctas: [], design_from: c.design_from,
    raw_html: rawHtml,
  };
  const onEditable = (p: Partial<EditableContent>) => {
    const out: Partial<Content> = {};
    if (p.format) out.mode = p.format === 'design' ? 'design' : 'custom';
    (['subject', 'body', 'text', 'design', 'design_from'] as const).forEach(k => {
      if (k in p) (out as any)[k] = p[k];
    });
    if ('raw_html' in p) setRawHtml(!!p.raw_html);
    onContent(out);
  };

  const written = c.mode === 'design' ? !!c.design?.blocks?.length : !!(c.subject.trim() || c.body.replace(/<[^>]*>/g, '').trim());
  const pick = (src: SourceContent, how: 'reference' | 'copy') => {
    setPicker(false);
    setSavedTpl(null);
    if (how === 'reference' && src.template_key) {
      const name = src.kind === 'campaign' ? (templates.find(t => t.key === src.template_key)?.name || src.template_key) : src.name;
      remember(src.template_key, name);
      onContent({ mode: 'template', key: src.template_key });
      setNote({ kind: 'ok', text: `Sending the template “${name}” itself.` });
      return;
    }
    if (written && c.mode !== 'template'
        && !window.confirm(`Replace what is written here with “${src.name}”? Your changes here are lost.`)) return;
    const design = src.format === 'design';
    const patch: Partial<Content> = {
      mode: design ? 'design' : 'custom',
      subject: src.subject || '',
      body: design ? '' : src.body || '',
      text: design ? '' : src.text || '',
      design: design && src.design ? withKeys(src.design) : null,
      design_from: design ? src.name : '',
    };
    if (src.from_email) Object.assign(patch, { from_email: src.from_email, from_name: src.from_name || '', reply_to: src.reply_to || '' });
    if (src.placeholder_fallbacks && Object.keys(src.placeholder_fallbacks).length) {
      patch.fallbacks = { ...c.fallbacks, ...src.placeholder_fallbacks };
    }
    setRawHtml(!design && !!src.raw_html);
    setSource({ ...src.source });
    onContent(patch);
    setNote(src.warnings?.length
      ? { kind: 'warn', text: `Copied “${src.name}”. ${src.warnings.join(' ')}` }
      : { kind: 'ok', text: `Copied “${src.name}”. Change it as you like; the original is not touched.` });
  };

  const copies = parseEmails(c.cc).length + parseEmails(c.bcc).length;
  return (
    <Section title="2 · Content">
      <div className="flex flex-wrap items-end gap-3">
        <Labeled label="Campaign name (required — how it is listed in Campaigns)">
          <input value={c.name} maxLength={200} onChange={e => onContent({ name: e.target.value })}
            placeholder="e.g. Oct 2026 · lapsed trials win-back" className={`${inputCls} !w-80`} />
        </Labeled>
        <div className="flex gap-1 pb-0.5">
          {([['template', 'Use a template'], ['design', 'Build a design'], ['custom', 'Write from scratch']] as const).map(([k, label]) => (
            <button key={k} type="button" onClick={() => onContent({ mode: k })}
              className={`py-1.5 px-3 rounded-full text-xs font-grotesk ${c.mode === k
                ? 'bg-primary-500 text-grayscale-100 font-bold'
                : 'border border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200'}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex gap-1 pb-0.5 ml-auto">
          <Btn small onClick={() => setPicker(true)}
            title="A saved template, a built-in e-mail, a past campaign or a design">Start from…</Btn>
          {c.mode !== 'template' && (
            <Btn small onClick={() => setSaveOpen(true)} disabled={!c.subject.trim() || !written}
              title="Keep this e-mail in Saved templates, for Campaigns and MEMAC">Save as a template…</Btn>
          )}
        </div>
      </div>
      {note && (note.kind === 'warn'
        ? <div className="text-xs rounded-lg px-3 py-2 border text-amber-800 bg-amber-50 border-amber-200">{note.text}</div>
        : <Msg kind={note.kind}>{note.text}</Msg>)}
      {savedTpl && c.mode !== 'template' && (
        <div className="flex flex-wrap items-center gap-2 text-xs rounded-lg px-3 py-2 border border-primary-300 bg-primary-50 text-grayscale-1300">
          Saved as “{savedTpl.name}”. Send it as that template, so the dashboard and the “has received” filters name it?
          <Btn small kind="primary" onClick={() => {
            remember(savedTpl.key, savedTpl.name);
            onContent({ mode: 'template', key: savedTpl.key });
            setSavedTpl(null);
          }}>Send it as the saved template</Btn>
          <Btn small onClick={() => setSavedTpl(null)}>Keep it written here</Btn>
        </div>
      )}

      {c.mode === 'template' ? (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-end gap-3">
            <Labeled label="Template">
              <Select value={c.key} onChange={v => onContent({ key: v })} options={allTemplates.map(t => t.key)}
                labels={Object.fromEntries(allTemplates.map(t =>
                  [t.key, `${t.name}${t.sent ? ` — sent ${num(t.sent)}×` : ''}`]))} />
            </Labeled>
            {active?.trigger && <div className="text-xs text-grayscale-1100 pb-2">Normally: {active.trigger}</div>}
          </div>
          <Check checked={skipReceived} onChange={onSkip}>
            Skip anyone who already received this template (recommended)
          </Check>
        </div>
      ) : (
        <ContentEditor value={editable} onChange={onEditable} formats={[editable.format]}
          placeholders={placeholders} allowSaveAsDesign
          saveAsDesignNote={`From the campaign “${c.name || c.subject}”`}
          hint={<div className="text-[11px] text-grayscale-1100">
            What someone without a value for a placeholder gets is set under “Placeholders in this e-mail” below.
          </div>} />
      )}

      {placeholdersPanel}

      <div className="border-t border-grayscale-200 pt-3">
        <EnvelopeFields value={{ from_email: c.from_email, from_name: c.from_name, reply_to: c.reply_to, cc: c.cc, bcc: c.bcc }}
          onChange={p => onContent(p as Partial<Content>)} senders={senders} sendersError={sendersError}
          allowTemplateDefault={c.mode === 'template'} showCcBcc />
      </div>
      {copies > 0 && (
        <div className="text-xs text-amber-800">
          Every recipient's copy carries these CC / BCC addresses, so each of them receives one copy per
          recipient{recipients ? ` — ${num(recipients)} each` : ''}.
        </div>
      )}
      {problem && <div className="text-xs text-red-700">{problem}</div>}

      {picker && (
        <TemplatePicker allowReference formats={['html', 'design']} onClose={() => setPicker(false)} onPick={pick}
          title="Start this campaign from…" />
      )}
      {saveOpen && (
        <SaveAsTemplate content={editable} fallbacks={c.fallbacks}
          envelope={{ from_email: c.from_email, from_name: c.from_name, reply_to: c.reply_to }}
          origin="campaigns" source={source} defaultName={c.name || c.subject}
          note="It goes to Saved templates, where Campaigns and MEMAC triggers can use it. This campaign is not sent."
          onClose={() => setSaveOpen(false)}
          onSaved={(t, w) => {
            setSaveOpen(false);
            setSavedTpl(t);
            setNote(w.length ? { kind: 'warn', text: `Saved “${t.name}”. ${w.join(' ')}` } : null);
          }} />
      )}
    </Section>
  );
};
